/* eslint-disable complexity */
import Big from 'big.js'
import { Dots } from 'metronome-ui/components/Dots'
import { useChainId } from 'metronome-ui/hooks/chains'
import { fromUnit } from 'metronome-utils'
import { useContext, useEffect } from 'react'

import { useWalletChainId } from '../hooks/useWalletChainId'

import { AppContext } from './AppContext/AppContext'
import CryptoNumber from './CryptoNumber'
import Dropdown from './Dropdown'
import IconContainer from './icons/IconContainer'
import { MetronomeContext } from './MetronomeContext'

const TokenSelector = function ({
  availableTokens = [],
  chooseLabel,
  className,
  disableWithoutBalance = true,
  disabled,
  displayBalances,
  itemsListClassName,
  setToken,
  token
}) {
  const { walletBalancesLoaded } = useContext(MetronomeContext)
  const chainId = useChainId()
  const walletChainId = useWalletChainId()
  const {
    state: {
      wallet: { connected }
    }
  } = useContext(AppContext)
  useEffect(
    function () {
      if (availableTokens.length === 1) {
        setToken(availableTokens[0])
      }
    },
    [availableTokens, setToken]
  )
  return (
    <Dropdown
      className={`whitespace-nowrap ${className}`}
      disabled={disabled}
      selector={
        availableTokens.length === 1 ? (
          <button
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-default'
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            type="button"
          >
            <IconContainer
              className="mr-3 h-6 w-6"
              name={availableTokens[0].symbol.toLowerCase()}
            />
            {availableTokens[0].symbol}
          </button>
        ) : token ? (
          <div
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            data-sentry="token-selector"
          >
            <IconContainer
              className="mr-3 h-6 w-6"
              name={token.symbol.toLowerCase()}
            />
            {token.symbol}
            <IconContainer className="ml-3 fill-current" name="caret" />
          </div>
        ) : (
          <button
            className={`${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            } bg-blue-tertiary flex items-center rounded-lg px-3 py-2 text-sm leading-6 text-white`}
            data-sentry="token-selector"
            type="button"
          >
            {chooseLabel}
            <IconContainer className="ml-3 fill-current" name="caret" />
          </button>
        )
      }
    >
      <div
        className={`bg-blue-tertiary absolute right-0 z-40 mt-3 w-72 rounded-lg ${itemsListClassName}`}
      >
        {availableTokens.length > 1 && (
          <ul>
            {availableTokens.map(function (availableToken) {
              const hasBalance =
                availableToken.balance &&
                Big(
                  fromUnit(availableToken.balance, availableToken.decimals)
                ).gt(0)
              const noSupply = Big(availableToken.availableSupply).lte(0)

              return (
                <li
                  className={`hover:bg-blue-4 m-2 cursor-pointer rounded-lg p-2 text-white ${
                    (displayBalances && !hasBalance && disableWithoutBalance) ||
                    noSupply
                      ? 'cursor-not-allowed opacity-50'
                      : ''
                  }`}
                  data-sentry="token-selector-item"
                  key={`${availableToken.address}${
                    availableToken.depositToken || ''
                  }`}
                  onClick={function (e) {
                    if (
                      (displayBalances &&
                        !hasBalance &&
                        disableWithoutBalance) ||
                      noSupply
                    ) {
                      e.stopPropagation()
                      return
                    }
                    setToken(availableToken)
                  }}
                >
                  <div className="flex justify-between">
                    <span className="flex">
                      <IconContainer
                        className="mr-2"
                        height={24}
                        name={availableToken.symbol.toLowerCase()}
                        width={24}
                      />
                      {availableToken.symbol}
                    </span>
                    <span className="text-sm">
                      {displayBalances &&
                        (chainId === walletChainId &&
                        connected &&
                        !walletBalancesLoaded ? (
                          <Dots />
                        ) : (
                          availableToken?.balance &&
                          (Big(availableToken.balance).gt(0) ? (
                            <CryptoNumber
                              value={Big(
                                fromUnit(
                                  availableToken.balance || 0,
                                  availableToken.decimals
                                )
                              )}
                            />
                          ) : (
                            <span className="mr-4">-</span>
                          ))
                        ))}
                    </span>
                  </div>
                  {availableToken.message && (
                    <p className="whitespace-normal pl-8 text-xs">
                      {availableToken.message}
                    </p>
                  )}
                  {availableToken.estimatedApy && (
                    <p className="whitespace-normal pl-8 text-xs">
                      {availableToken.estimatedApy.text}:{' '}
                      {availableToken.estimatedApy.apy}% -{' '}
                      {availableToken.estimatedApy.maxLeveragedApy.lt(0) ? (
                        <span className="text-red-600">
                          {availableToken.estimatedApy.maxLeveragedApy.toFixed()}
                          %
                        </span>
                      ) : (
                        <>
                          {availableToken.estimatedApy.maxLeveragedApy.toFixed()}
                          %
                        </>
                      )}
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Dropdown>
  )
}

export default TokenSelector
